import React, { useState, useEffect } from "react";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import { makeStyles, Paper, Tabs, Tab } from "@material-ui/core";

import TabPanel from "../../components/TabPanel";

import CompanySchedules from "../../components/Settings/CompanySchedules";
import useWhatsApps from "../../hooks/useWhatsApps";
import CompaniesManager from "../../components/CompaniesManager";
import PlansManager from "../../components/PlansManager";
import HelpsManager from "../../components/HelpsManager";
import Options from "../../components/Settings/Options";

import { i18n } from "../../translate/i18n.js";
import { toast } from "react-toastify";
import toastError from "../../errors/toastError";

import useCompanies from "../../hooks/useCompanies";
import useAuth from "../../hooks/useAuth.js";
import useSettings from "../../hooks/useSettings";

import OnlyForSuperUser from "../../components/OnlyForSuperUser";
import ContactFieldTemplatesManager from "../../components/ContactFieldTemplatesManager";

const useStyles = makeStyles((theme) => ({
  root: {
    flex: 1,
    backgroundColor: theme.palette.background.paper,
  },
  mainPaper: {
    ...theme.scrollbarStyles,
    overflowY: "scroll",
    flex: 1,
  },
  tab: {
    backgroundColor: theme.palette.options,
    borderRadius: 4,
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(1),
  },
  tabsBar: {
    margin: theme.spacing(1, 1, 0),
    "& .MuiTabs-indicator": { display: "none" },
  },
  pillTab: {
    textTransform: "none",
    fontWeight: 600,
    fontSize: 13,
    minHeight: 0,
    minWidth: 0,
    borderRadius: 999,
    padding: theme.spacing(0.75, 1.75),
    border: "1px solid transparent",
    color: theme.palette.text.secondary,
    opacity: 1,
    "&.Mui-selected": {
      color: theme.palette.text.primary,
      backgroundColor: theme.palette.background.paper,
      borderColor: theme.palette.divider,
    },
  },
  paper: {
    ...theme.scrollbarStyles,
    padding: theme.spacing(3),
    width: "100%",
    [theme.breakpoints.down("xs")]: { padding: theme.spacing(1, 2, 2) },
  },
}));

const SettingsCustom = () => {
  const classes = useStyles();
  const [tab, setTab] = useState("options");
  const [schedules, setSchedules] = useState([]);
  const [company, setCompany] = useState({});
  const [loading, setLoading] = useState(false);
  const [currentUser, setCurrentUser] = useState({});
  const [settings, setSettings] = useState([]);
  const [schedulesEnabled, setSchedulesEnabled] = useState(false);
  const [companyMode, setCompanyMode] = useState(false);

  const { getCurrentUserInfo } = useAuth();
  const { find, updateSchedules } = useCompanies();
  const { getAll: getAllSettings } = useSettings();

  // Carrega dados apenas uma vez
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const companyId = localStorage.getItem("companyId");
        const [companyData, settingsList, userData] = await Promise.all([
          find(companyId),
          getAllSettings(),
          getCurrentUserInfo(),
        ]);

        setCompany(companyData);
        setSchedules(companyData.schedules || []);
        setSettings(settingsList);
        setCurrentUser(userData);

        if (Array.isArray(settingsList)) {
          const scheduleType = settingsList.find((s) => s.key === "scheduleType");
          const isEnabled = scheduleType ? scheduleType.value === "company" : false;
          setSchedulesEnabled(isEnabled);
          setCompanyMode(isEnabled);

          // Se horários estiverem habilitados, define a aba correta
          if (isEnabled && tab === "options") {
            // Não muda a aba, mas garante que está pronto
          }
        }
      } catch (err) {
        toast.error("Erro ao carregar configurações.");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleTabChange = (event, newValue) => {
    if (["options", "schedules", "contactFields", "companies", "plans", "helps"].includes(newValue)) {
      // Só permite mudar para "schedules" se estiver habilitado
      if (newValue === "schedules" && !schedulesEnabled) {
        return;
      }
      setTab(newValue);
    }
  };

  // A mensagem de fora do expediente do modo por empresa fica em cada conexão.
  const { whatsApps } = useWhatsApps();
  const hasRestriction = schedules.some(
    (s) => s.closed === true || (s.startTime && s.endTime)
  );
  const withoutMessage = whatsApps.filter(
    (w) => !(w.outOfHoursMessage || "").trim()
  );
  const warnNames = hasRestriction ? withoutMessage.map((w) => w.name) : [];

  const handleSubmitSchedules = async (data) => {
    setLoading(true);
    try {
      await updateSchedules({ id: company.id, schedules: data });
      setSchedules(data);
      toast.success("Horários atualizados com sucesso.");
    } catch (err) {
      toastError(err);
    }
    setLoading(false);
  };

  const isSuper = () => currentUser.super;
  const isAdmin = () => currentUser.profile === "admin";

  return (
    <MainContainer className={classes.root}>
      <MainHeader>
        <Title>{i18n.t("settings.title")}</Title>
      </MainHeader>

      <Paper className={classes.mainPaper} elevation={1}>
        {/* Abas principais. scrollButtons="auto": setas só no computador; no
            celular as abas rolam com o dedo (com "on" a seta da esquerda
            ocupava um espaço vazio). */}
        <Tabs
          value={tab}
          indicatorColor="primary"
          textColor="primary"
          scrollButtons="auto"
          variant="scrollable"
          onChange={handleTabChange}
          className={classes.tabsBar}
        >
          <Tab label="Opções" value="options" className={classes.pillTab} />
          
          {/* Sempre renderiza o Tab, mas oculta se desabilitado */}
          <Tab
            label="Horários"
            value="schedules"
            className={classes.pillTab}
            disabled={!schedulesEnabled}
            style={{ display: schedulesEnabled ? "flex" : "none" }}
          />
          
          {isAdmin() && <Tab label="Ficha do contato" value="contactFields" className={classes.pillTab} />}
          {isSuper() && <Tab label="Empresas" value="companies" className={classes.pillTab} />}
          {isSuper() && <Tab label="Planos" value="plans" className={classes.pillTab} />}
          {isSuper() && <Tab label="Ajuda" value="helps" className={classes.pillTab} />}
        </Tabs>

        {/* Conteúdo das abas */}
        <Paper className={classes.paper} elevation={0}>
          <TabPanel value={tab} name="options">
            <Options
              settings={settings}
              onSaved={(key, value) =>
                setSettings((prev) => {
                  const list = Array.isArray(prev) ? prev : [];
                  return list.some((item) => item.key === key)
                    ? list.map((item) => (item.key === key ? { ...item, value } : item))
                    : [...list, { key, value }];
                })
              }
              scheduleTypeChanged={(value) => {
                //setSchedulesEnabled(value === "company");
                setCompanyMode(value);
                setSchedulesEnabled(true); //deixado sempre true 
                // Se desabilitar, volta para "options"
                if (!value && tab === "schedules") {
                  setTab("options");
                }
              }}
            />
          </TabPanel>

          {/* Só renderiza o conteúdo se habilitado */}
          {schedulesEnabled && (
            <TabPanel value={tab} name="schedules">
              <CompanySchedules
                loading={loading}
                onSubmit={handleSubmitSchedules}
                initialValues={schedules}
                companyMode={companyMode}
                onGoToOptions={() => setTab("options")}
                connectionsWithoutMessage={warnNames}
              />
            </TabPanel>
          )}

          {isAdmin() && (
            <TabPanel value={tab} name="contactFields">
              <ContactFieldTemplatesManager />
            </TabPanel>
          )}

          <OnlyForSuperUser
            user={currentUser}
            yes={() => (
              <TabPanel value={tab} name="companies">
                <CompaniesManager />
              </TabPanel>
            )}
          />

          <OnlyForSuperUser
            user={currentUser}
            yes={() => (
              <TabPanel value={tab} name="plans">
                <PlansManager />
              </TabPanel>
            )}
          />

          <OnlyForSuperUser
            user={currentUser}
            yes={() => (
              <TabPanel value={tab} name="helps">
                <HelpsManager />
              </TabPanel>
            )}
          />
        </Paper>
      </Paper>
    </MainContainer>
  );
};

export default SettingsCustom;